<script lang="ts">
	import { maxCharactersPerLabel, maxLabels } from "../../../../core/schema/productOptionsConstants.js";
	import { inflectFit } from "./inflectFit.js";
	import { inflectLabel } from "./inflectLabel.js";
	import { inflectLabelCount } from "./inflectLabelCount.js";
	import LabelledInput from "../Form/LabelledInput.svelte";
	import type { ProductLabel } from "../../../../core/schema/ProductLabel.js";
	import { ProductLabelStatus } from "../../../../core/schema/ProductLabelStatus.js";
	import { inflectCharacter } from "./inflectCharacter.js";
	import cedulkydort from "../../../../core/assets/icons/cedulkydort.svg";
	import Button from "../Button.svelte";
	import plus from "../../../../core/assets/icons/btn-plus.svg?raw";
	import ProductOptionHeader from "./ProductOptionHeader.svelte";
	import { calculatePriceForProductOption } from "../../../../core/schema/utils/calculatePriceForProductOption.js";
	import { ProductOption } from "../../../../core/schema/ProductOption.js";

	export let labels: ProductLabel[];
	export let updateLabels: (newLabels: ProductLabel[]) => void;
	export let basePrice: number | undefined;
	export let minimalPricesForOptions: Record<ProductOption, number>;
	export let minimalPriceIsFinal = false;

	let newLabels = structuredClone(labels);

	function addLabel(): (typeof newLabels)[number] {
		const label: ProductLabel = { id: crypto.randomUUID(), text: "", status: ProductLabelStatus.ToDo };
		newLabels.push(label);
		newLabels = newLabels;
		return label;
	}

	function spreadLabels(trimmedValue: string): void {
		const words = trimmedValue.split(/\s+/u);

		let labelIndex = 0;
		const [label] = newLabels;
		if (label) {
			label.text = "";
		}

		do {
			let label = newLabels[labelIndex];
			const word = words.shift();
			if (!label) {
				label = addLabel();
			}
			if (!word) {
				break;
			}

			const newText = [label.text, word].filter(Boolean).join(" ");
			const newTextFits = newText.length <= maxCharactersPerLabel;
			/*
			 * Fill in the last label with the remaining text.
			 * Validations will stop the user from submitting the form with too long labels.
			 */
			const atLastLabel = labelIndex === maxLabels - 1;
			if (newTextFits || atLastLabel) {
				label.text = newText;
			} else {
				labelIndex++;
				words.unshift(word);
			}
		} while (labelIndex < maxLabels);

		newLabels = newLabels;
	}

	function trimLabel(target: EventTarget | null, label: ProductLabel): string {
		const { value } = target as HTMLInputElement;
		label.text = value.trim();
		newLabels = newLabels;
		return label.text;
	}

	let firstLabelIsForced = true;

	function removeLabel(idToRemove: string): void {
		newLabels = newLabels.filter(({ id }) => id !== idToRemove);
	}

	function onLabelInput(target: EventTarget | null, validate = false): void {
		const input = target as HTMLInputElement;
		const usedCharacters = input.value.trim().length;
		if (usedCharacters === 0) {
			input.setCustomValidity("Nápis na cedulku je povinný.");
		} else if (usedCharacters > maxCharactersPerLabel && newLabels.length > 1) {
			input.setCustomValidity(
				`Cedulka může mít maximálně ${maxCharactersPerLabel} ${inflectCharacter(maxCharactersPerLabel)}. Aktuálně využíváte ${usedCharacters} ${inflectCharacter(usedCharacters)}.`,
			);
		} else {
			input.setCustomValidity("");
		}
		if (validate) {
			input.reportValidity();
		}
	}

	let spreadTimeout: number | undefined;

	function onLabelChange(target: EventTarget | null, label: ProductLabel): void {
		clearTimeout(spreadTimeout);
		spreadTimeout = undefined;
		const value = trimLabel(target, label);
		const index = newLabels.indexOf(label);
		if (index === 0 && newLabels.length <= 1) {
			spreadTimeout = window.setTimeout(() => {
				spreadLabels(value);
				setTimeout(() => {
					const inputs = document.querySelectorAll<HTMLInputElement>("input[id^=label-]");
					for (const input of inputs) {
						if (!input.value) {
							continue;
						}
						onLabelInput(input, true);
						input.dispatchEvent(new Event("change", { bubbles: true }));
					}
				});
				spreadTimeout = undefined;
			}, 125);
		} else {
			onLabelInput(target, true);
		}
	}

	function forceFirstLabel(labels: ProductLabel[]): void {
		if (labels.length === 0 && firstLabelIsForced) {
			addLabel();
			firstLabelIsForced = false;
		}
	}

	$: forceFirstLabel(newLabels);

	$: minimalPrice = minimalPricesForOptions[ProductOption.Labels];
	$: currentPrice =
		basePrice !== undefined ? calculatePriceForProductOption(basePrice, ProductOption.Labels, newLabels) : minimalPrice;
	$: labelsLeft = maxLabels - newLabels.length;
</script>

<ProductOptionHeader
	name="Přidat cedulku"
	image={cedulkydort}
	price={currentPrice}
	{minimalPrice}
	{minimalPriceIsFinal}
>
	<div>Ručně psané jméno nebo krátký vzkaz na našich čokoládových destičkách z dortu udělá zároveň i krásné gesto.</div>
</ProductOptionHeader>
<div class="grid w-full grid-cols-[auto,1fr] items-center gap-x-3 gap-y-2">
	{#each newLabels as label, index (label.id)}
		<span class="text-base font-semibold leading-5">{index + 1}.</span>
		<div class="flex w-full gap-2">
			<LabelledInput
				id="label-{label.id}"
				placeholder="Vložte nápis na cedulku"
				class="flex-1"
				required
				fullWidth
				clearable
				showValid
				on:clear={() => {
					clearTimeout(spreadTimeout);
					spreadTimeout = undefined;
					removeLabel(label.id);
				}}
				alwaysEnableClear
				bind:value={label.text}
				inputProps={{
					// Prevents user from entering more characters than allowed manually. Also blocks when spread timeout is active.
					maxlength: newLabels.length === 1 && spreadTimeout === undefined ? undefined : maxCharactersPerLabel,
				}}
				on:input={(event) => {
					onLabelInput(event.target);
				}}
				on:change={(event) => {
					onLabelChange(event.target, label);
				}}
				withoutLabel
			/>
		</div>
	{/each}
</div>
<div class="flex flex-col">
	{#if labelsLeft > 0}
		<div class="col-start-2 flex flex-col gap-1 pt-2">
			<span class="text-2xs leading-4">
				Na produkt se
				{inflectFit(labelsLeft)}
				ještě
				{labelsLeft}
				{inflectLabel(labelsLeft)}.
			</span>
			<button type="button" class="flex items-start" on:click={addLabel}>
				<span class="text-2xs leading-4 underline">Přidat {inflectLabelCount[newLabels.length + 1]} cedulku</span>
			</button>
		</div>
	{/if}
	<Button
		icon={plus}
		iconClass="w-5 h-5"
		class="mt-4 self-end"
		on:click={() => {
			updateLabels(newLabels);
		}}
	>
		Přidat úpravu
	</Button>
</div>
